import React from 'react';
import { SearchXIcon } from 'lucide-react';
import type { RecalculatedLoan } from '../types/comp';
import { formatCurrency, formatDate, formatSigned } from '../utils/commissions';
interface LoanPreviewTableProps {
  loans: RecalculatedLoan[];
  ready: boolean;
}
export function LoanPreviewTable({
  loans,
  ready
}: LoanPreviewTableProps) {
  if (!ready) {
    return <EmptyPanel title="Set a rate to preview" body="Enter a bps rate and short-loan percentage to see what each loan recalculates to." data-id="element-48" data-mp="3r3zhpsqwrzu2" />;
  }
  if (loans.length === 0) {
    return <EmptyPanel title="No loans in this funded-date range" body="Widen the funded-date range to include loans for this employee." data-id="element-49" data-mp="3d5rbsvbw9o6r" />;
  }
  const net = loans.reduce((sum, loan) => sum + loan.difference, 0);
  return <div className="overflow-x-auto" data-id="element-50" data-mp="2kof8wkr3bjgb">
      <table className="w-full min-w-[620px] border-collapse text-left" data-id="element-51" data-mp="2kndnpngd1gkv">
        <thead data-id="element-52" data-mp="2fqdgvabos9sp">
          <tr className="border-b border-surface-line bg-surface-deep text-[11px] font-semibold uppercase tracking-wider text-ink-600" data-id="element-53" data-mp="2fosxmgutoda5">
            <th scope="col" className="py-2 pl-5 pr-3 font-semibold" data-id="element-54" data-mp="17hstpte3vbct">
              Loan
            </th>
            <th scope="col" className="px-3 py-2 font-semibold" data-id="element-55" data-mp="1c8snwqvubfbm">
              Funded
            </th>
            <th scope="col" className="px-3 py-2 text-right font-semibold" data-id="element-56" data-mp="1h8f3h6z557nl">
              Current
            </th>
            <th scope="col" className="px-3 py-2 text-right font-semibold" data-id="element-57" data-mp="jlonkz7ozp66">
              New
            </th>
            <th scope="col" className="py-2 pl-3 pr-5 text-right font-semibold" data-id="element-58" data-mp="ojzwn7q5xvnc">
              Difference
            </th>
          </tr>
        </thead>
        <tbody data-id="element-59" data-mp="xko1pcxkwlo3">
          {loans.map(loan => <tr key={loan.id} className="border-b border-surface-line last:border-b-0" data-id="element-60" data-mp="xlhbynvqxtaz">
              <th scope="row" className="py-2.5 pl-5 pr-3 text-left font-normal" data-id="element-61" data-mp="24x6lj5j0o9mn">
                <span className="block text-[13px] font-medium text-ink-900" data-id="element-62" data-mp="28ya3bxkmkfqi">{loan.name}</span>
                <span className="text-[11px] text-ink-500" data-id="element-63" data-mp="9ticxkn2kwtq">
                  {loan.id} · {loan.loanType === 'full' ? 'Full' : 'Short'} ·{' '}
                  {formatCurrency(loan.amount)}
                </span>
              </th>
              <td className="px-3 py-2.5 text-[13px] tabular-nums text-ink-700" data-id="element-64" data-mp="iuggk1tlj2bs">
                {formatDate(loan.fundedDate)}
              </td>
              <td className="px-3 py-2.5 text-right text-[13px] tabular-nums text-ink-700" data-id="element-65" data-mp="3mbgjko8n4mso">
                {formatCurrency(loan.currentCommission)}
              </td>
              <td className="px-3 py-2.5 text-right text-[13px] font-medium tabular-nums text-ink-900" data-id="element-66" data-mp="3i8jvwj48u0hv">
                {formatCurrency(loan.newCommission)}
              </td>
              <td className={`py-2.5 pl-3 pr-5 text-right text-[13px] font-semibold tabular-nums ${loan.difference > 0 ? 'text-success-fg' : loan.difference < 0 ? 'text-danger-fg' : 'text-ink-400'}`} data-id="element-67" data-mp="3r9i0b2ilga0z">
                {formatSigned(loan.difference)}
              </td>
            </tr>)}
        </tbody>
        <tfoot data-id="element-68" data-mp="154g8sdlpuprt">
          <tr className="border-t border-surface-line bg-surface-deep" data-id="element-69" data-mp="154yd7xij8aat">
            <th scope="row" colSpan={4} className="py-2.5 pl-5 pr-3 text-right text-[12px] font-semibold text-ink-700" data-id="element-70" data-mp="1a28cs5zmy2cd">
              Net change
            </th>
            <td className={`py-2.5 pl-3 pr-5 text-right text-[13px] font-semibold tabular-nums ${net >= 0 ? 'text-success-fg' : 'text-danger-fg'}`} data-id="element-71" data-mp="21swa16y6unkr">
              {formatSigned(net)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>;
}
function EmptyPanel({
  title,
  body
}: {
  title: string;
  body: string;
}) {
  return <div className="flex flex-col items-center px-6 py-16 text-center" data-id="element-72" data-mp="hf40dfy7qlqk">
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-surface-deep text-ink-500" data-id="element-73" data-mp="wnhb1cykromt">
        <SearchXIcon className="h-5 w-5" aria-hidden="true" data-id="element-74" data-mp="rwhfoc53temf" />
      </span>
      <h3 className="mt-3.5 text-[14px] font-semibold text-ink-900" data-id="element-75" data-mp="nvfc5j45mgen">{title}</h3>
      <p className="mt-1 max-w-sm text-[13px] leading-relaxed text-ink-500" data-id="element-76" data-mp="ntba76ptfrh0">{body}</p>
    </div>;
}